import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import ActionButtonModal, {
  ActionButtonModalPropType,
} from './ActionButtonModal';

type AdditionalProps<Type extends 'client' | 'company'> = {
  descriptionMeta: Type extends 'client'
    ? {
        companyName?: never;
        givenName: string | undefined;
        familyName: string | undefined;
      }
    : {
        companyName: string | undefined;
        givenName?: never;
        familyName?: never;
      };
};

type ConfirmDeleteModalProps<Type extends 'client' | 'company'> =
  AdditionalProps<Type> &
    Omit<
      ActionButtonModalPropType,
      'onPositiveActionPerformed' | 'onClose' | 'title' | 'mainComponent'
    > &
    Partial<Pick<ActionButtonModalPropType, 'title' | 'positiveAction'>> & {
      description?: string;
      onClose: () => void;
      onConfirmDelete: () => void;
      type: Type;
    };

export function ConfirmDeleteModal<Type extends 'client' | 'company'>({
  description,
  descriptionMeta,
  onConfirmDelete,
  positiveAction = true,
  type,
  ...props
}: ConfirmDeleteModalProps<Type>) {
  const { t } = useTranslation();

  let mainComponent: ReactElement | string = description || <></>;

  if (!description) {
    switch (type) {
      case 'client':
        mainComponent = t(
          ($) => $.messages.client_delete_confirmation.description,
          {
            clientName: `${descriptionMeta.givenName || ''} ${
              descriptionMeta.familyName || ''
            }`.trim(),
          },
        );
        break;
      case 'company':
        mainComponent = t(
          ($) => $.messages.company_delete_confirmation.description,
          {
            companyName: descriptionMeta.companyName,
          },
        );
        break;
    }
  }

  return (
    <ActionButtonModal
      {...props}
      positiveAction={positiveAction}
      onPositiveActionPerformed={onConfirmDelete}
      title={
        type === 'client'
          ? t(($) => $.messages.client_delete_confirmation.title)
          : t(($) => $.messages.company_delete_confirmation.title)
      }
      positiveActionText="Confirm"
      mainComponent={mainComponent}
    />
  );
}
